/**
 * Patient Grid Columns - Measure-Specific Column Configurations
 *
 * Provides dynamic column definitions for the patient drilldown grid based on
 * the selected quality measure. Each measure has its own set of clinically relevant
 * data columns with proper formatting, sorting, and visual indicators.
 */

import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { getPassFailBadgeVariant } from '@/lib/equip-utils'
import type { PatientDrilldownItem } from '@/lib/equip-types'
import { SortableHeader } from '@/views/equip/components/SortableHeader'

const columnHelper = createColumnHelper<PatientDrilldownItem>()

/**
 * Format date as "MM/DD/YYYY" or em-dash for missing values
 */
function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '\u2014'
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  } catch {
    return '\u2014'
  }
}

/**
 * Get earliest non-null date from multiple date fields
 */
function getEarliestDate(...dates: (string | undefined)[]): string | undefined {
  const validDates = dates
    .filter((d): d is string => !!d)
    .map(d => new Date(d))
    .filter(d => !isNaN(d.getTime()))

  if (validDates.length === 0) return undefined

  const earliest = new Date(Math.min(...validDates.map(d => d.getTime())))
  return earliest.toISOString()
}

/**
 * Common columns used across all measures
 */
function getCommonColumns(): ColumnDef<PatientDrilldownItem, any>[] {
  return [
    columnHelper.accessor('patientName', {
      header: ({ column }) => <SortableHeader label="Patient Name" column={column} />,
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium">
            {row.original.patientName || row.original.providerName || 'Unknown'}
          </span>
          {row.original.isAco && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-500/25 dark:text-blue-300 leading-none">
              ACO
            </span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('facilityName', {
      header: ({ column }) => <SortableHeader label="Facility" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.facilityName || row.original.facilityId || 'Unknown'}
        </span>
      ),
    }),
  ]
}

/**
 * AWV (Annual Wellness Visit) columns
 * Data sourced from cqm_awv_data via drilldown API
 */
function getAWVColumns(): ColumnDef<PatientDrilldownItem, any>[] {
  return [
    ...getCommonColumns(),
    columnHelper.accessor('awv', {
      id: 'eligibleMonth',
      header: ({ column }) => <SortableHeader label="Eligible Month" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">{row.original.awv || 'N/A'}</span>
      ),
    }),
    columnHelper.accessor('awvStatus', {
      id: 'awvStatus',
      header: ({ column }) => <SortableHeader label="AWV Status" column={column} />,
      cell: ({ row }) => {
        const status = row.original.awvStatus || ''
        const isCompleted = status.startsWith('Completed')
        return (
          <Badge variant={isCompleted ? 'lightSuccess' : 'lightError'} className="text-xs">
            {status || 'Unknown'}
          </Badge>
        )
      },
    }),
    columnHelper.accessor('dateScheduled', {
      header: ({ column }) => <SortableHeader label="Date Scheduled" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">{formatDate(row.original.dateScheduled)}</span>
      ),
    }),
    columnHelper.accessor('awvDate', {
      header: ({ column }) => <SortableHeader label="Last AWV Date" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">{formatDate(row.original.awvDate)}</span>
      ),
    }),
  ]
}

/**
 * A1C (Diabetes Control) columns
 */
function getA1CColumns(): ColumnDef<PatientDrilldownItem, any>[] {
  return [
    ...getCommonColumns(),
    columnHelper.accessor('a1cDate', {
      header: ({ column }) => <SortableHeader label="Last A1C Date" column={column} />,
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.a1cDate)}</span>,
    }),
    columnHelper.accessor('a1cValue', {
      id: 'a1cValue',
      header: ({ column }) => <SortableHeader label="A1C Value" column={column} />,
      cell: ({ row }) => {
        const value = row.original.a1cValue ?? row.original.consolidatedA1cValue
        const isControlled = value && value < 9.0
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{value?.toFixed(1) ?? 'N/A'}</span>
            {value !== undefined && (
              <Badge variant={isControlled ? 'lightSuccess' : 'lightError'} className="text-xs">
                {isControlled ? 'Controlled' : 'Uncontrolled'}
              </Badge>
            )}
          </div>
        )
      },
    }),
    columnHelper.accessor('diabetesMissingComponent', {
      header: 'Missing Component',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.diabetesMissingComponent || '-'}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'status',
      header: 'Met',
      cell: ({ row }) => (
        <Badge variant={getPassFailBadgeVariant(row.original.isMet)}>
          {row.original.isMet ? 'Met' : 'Unmet'}
        </Badge>
      ),
    }),
  ]
}

/**
 * BCS (Breast Cancer Screening) columns
 */
function getBCSColumns(): ColumnDef<PatientDrilldownItem, any>[] {
  return [
    ...getCommonColumns(),
    columnHelper.display({
      id: 'lastScreeningDate',
      header: ({ column }) => <SortableHeader label="Last Screening Date" column={column} />,
      cell: ({ row }) => {
        const earliestDate = getEarliestDate(
          row.original.bcsClaimsScreeningDate,
          row.original.bcsLabScreeningDate,
          row.original.bcsCounselingScreeningDate,
          row.original.bcsProgressScreeningDate
        )
        return <span className="text-sm">{formatDate(earliestDate)}</span>
      },
    }),
    columnHelper.accessor('bcsStatus', {
      header: ({ column }) => <SortableHeader label="BCS Status" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.bcsStatus || 'Unknown'}
        </span>
      ),
    }),
    columnHelper.accessor('bcsMissingComponent', {
      header: 'Missing Component',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.bcsMissingComponent || '-'}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'status',
      header: 'Met',
      cell: ({ row }) => (
        <Badge variant={getPassFailBadgeVariant(row.original.isMet)}>
          {row.original.isMet ? 'Met' : 'Unmet'}
        </Badge>
      ),
    }),
  ]
}

/**
 * CRCS (Colorectal Cancer Screening) columns
 */
function getCRCSColumns(): ColumnDef<PatientDrilldownItem, any>[] {
  return [
    ...getCommonColumns(),
    columnHelper.accessor('crcsLastScreeningDate', {
      header: ({ column }) => <SortableHeader label="Last Screening Date" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">{formatDate(row.original.crcsLastScreeningDate)}</span>
      ),
    }),
    columnHelper.accessor('crcsLastScreeningModality', {
      header: ({ column }) => <SortableHeader label="Modality" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.crcsLastScreeningModality || '—'}
        </span>
      ),
    }),
    columnHelper.accessor('crcsStatus', {
      header: ({ column }) => <SortableHeader label="CRCS Status" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.crcsStatus || 'Unknown'}
        </span>
      ),
    }),
    columnHelper.accessor('crcsMissingComponent', {
      header: 'Missing Component',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.crcsMissingComponent || '-'}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'status',
      header: 'Met',
      cell: ({ row }) => (
        <Badge variant={getPassFailBadgeVariant(row.original.isMet)}>
          {row.original.isMet ? 'Met' : 'Unmet'}
        </Badge>
      ),
    }),
  ]
}

/**
 * PHQ9 (Depression Screening) columns
 */
function getPHQ9Columns(): ColumnDef<PatientDrilldownItem, any>[] {
  return [
    ...getCommonColumns(),
    columnHelper.accessor('lastPhq9Date', {
      header: ({ column }) => <SortableHeader label="Last PHQ9 Date" column={column} />,
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.lastPhq9Date)}</span>,
    }),
    columnHelper.accessor('phq9TotalScore', {
      header: ({ column }) => <SortableHeader label="PHQ9 Score" column={column} />,
      cell: ({ row }) => {
        const score = row.original.phq9TotalScore
        let severity = 'Unknown'
        let variant: 'default' | 'lightSuccess' | 'lightWarning' | 'lightError' = 'default'

        if (score !== undefined && score !== null) {
          if (score < 5) {
            severity = 'None'
            variant = 'lightSuccess'
          } else if (score < 10) {
            severity = 'Mild'
            variant = 'lightSuccess'
          } else if (score < 15) {
            severity = 'Moderate'
            variant = 'lightWarning'
          } else if (score < 20) {
            severity = 'Moderate-Severe'
            variant = 'lightError'
          } else {
            severity = 'Severe'
            variant = 'lightError'
          }
        }

        return (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{score ?? 'N/A'}</span>
            {score !== undefined && score !== null && (
              <Badge variant={variant} className="text-xs">
                {severity}
              </Badge>
            )}
          </div>
        )
      },
    }),
    columnHelper.display({
      id: 'enrolledStatus',
      header: 'Psych Med Enrolled',
      cell: ({ row }) => {
        const pcpEnrolled = row.original.pcpEnrolledFlag
        const psychEnrolled = row.original.psychiatryEnrolledFlag
        const enrolled = pcpEnrolled || psychEnrolled

        return (
          <Badge variant={enrolled ? 'lightSuccess' : 'lightError'}>
            {enrolled ? (pcpEnrolled && psychEnrolled ? 'Both' : pcpEnrolled ? 'PCP' : 'Psych') : 'Not Enrolled'}
          </Badge>
        )
      },
    }),
    columnHelper.display({
      id: 'status',
      header: 'Met',
      cell: ({ row }) => (
        <Badge variant={getPassFailBadgeVariant(row.original.isMet)}>
          {row.original.isMet ? 'Met' : 'Unmet'}
        </Badge>
      ),
    }),
  ]
}

/**
 * HTN (Hypertension Control) columns
 */
function getHTNColumns(): ColumnDef<PatientDrilldownItem, any>[] {
  return [
    ...getCommonColumns(),
    columnHelper.accessor('lastBpDate', {
      header: ({ column }) => <SortableHeader label="Last BP Date" column={column} />,
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.lastBpDate)}</span>,
    }),
    columnHelper.display({
      id: 'bpReading',
      header: ({ column }) => <SortableHeader label="BP Reading" column={column} />,
      cell: ({ row }) => {
        const systolic = row.original.systolicBp
        const diastolic = row.original.diastolicBp

        if (!systolic || !diastolic) {
          return <span className="text-sm text-muted-foreground">N/A</span>
        }

        // Controlled: <140/90
        const isControlled = systolic < 140 && diastolic < 90

        return (
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono font-semibold">
              {systolic}/{diastolic}
            </span>
            <Badge variant={isControlled ? 'lightSuccess' : 'lightError'} className="text-xs">
              {isControlled ? 'Controlled' : 'Uncontrolled'}
            </Badge>
          </div>
        )
      },
    }),
    columnHelper.accessor('htnMissingComponent', {
      header: 'Missing Component',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.htnMissingComponent || '-'}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'status',
      header: 'Met',
      cell: ({ row }) => (
        <Badge variant={getPassFailBadgeVariant(row.original.isMet)}>
          {row.original.isMet ? 'Met' : 'Unmet'}
        </Badge>
      ),
    }),
  ]
}

/**
 * ACP (Advanced Care Planning) columns
 */
function getACPColumns(): ColumnDef<PatientDrilldownItem, any>[] {
  return [
    ...getCommonColumns(),
    columnHelper.accessor('acpMeasureMetDate', {
      header: ({ column }) => <SortableHeader label="ACP Met Date" column={column} />,
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.acpMeasureMetDate)}</span>,
    }),
    columnHelper.accessor('acpMissingComponent', {
      header: 'Missing Component',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.acpMissingComponent || '-'}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'status',
      header: 'Met',
      cell: ({ row }) => (
        <Badge variant={getPassFailBadgeVariant(row.original.isMet)}>
          {row.original.isMet ? 'Met' : 'Unmet'}
        </Badge>
      ),
    }),
  ]
}

/**
 * ACO_Visits (PC Physician ACO Visit Completion) columns
 */
function getAcoVisitsColumns(): ColumnDef<PatientDrilldownItem, any>[] {
  return [
    ...getCommonColumns(),
    columnHelper.accessor('acoName', {
      header: ({ column }) => <SortableHeader label="ACO Organization" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{row.original.acoName || '—'}</span>
      ),
    }),
    columnHelper.accessor('acoPhysicianAssigned', {
      header: ({ column }) => <SortableHeader label="Assigned Physician" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">{row.original.acoPhysicianAssigned || '—'}</span>
      ),
    }),
    columnHelper.accessor('acoDateScheduled', {
      header: ({ column }) => <SortableHeader label="Date Scheduled" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">{formatDate(row.original.acoDateScheduled ?? undefined)}</span>
      ),
    }),
    columnHelper.accessor('lastPcPhysicianVisit', {
      header: ({ column }) => <SortableHeader label="Last Visit Date" column={column} />,
      cell: ({ row }) => (
        <span className="text-sm">{formatDate(row.original.lastPcPhysicianVisit ?? undefined)}</span>
      ),
    }),
    columnHelper.accessor('acoVisitStatus', {
      header: ({ column }) => <SortableHeader label="Visit Status" column={column} />,
      cell: ({ row }) => {
        const status = row.original.acoVisitStatus
        if (!status) return <span className="text-sm text-muted-foreground">—</span>
        const variant = status === 'complete' ? 'lightSuccess' : 'lightWarning'
        return (
          <Badge variant={variant} className="text-xs">
            {status}
          </Badge>
        )
      },
    }),
    columnHelper.display({
      id: 'status',
      header: 'Met',
      cell: ({ row }) => (
        <Badge variant={getPassFailBadgeVariant(row.original.isMet)}>
          {row.original.isMet ? 'Met' : 'Unmet'}
        </Badge>
      ),
    }),
  ]
}

/**
 * Main export: Get columns for a specific measure
 */
export function getPatientGridColumns(measureName: string): ColumnDef<PatientDrilldownItem, any>[] {
  const measure = measureName.toUpperCase()

  switch (measure) {
    case 'AWV':
      return getAWVColumns()
    case 'A1C':
      return getA1CColumns()
    case 'BCS':
      return getBCSColumns()
    case 'CRCS':
      return getCRCSColumns()
    case 'PHQ9':
      return getPHQ9Columns()
    case 'HTN':
      return getHTNColumns()
    case 'ACP':
      return getACPColumns()
    case 'ACO_VISITS':
      return getAcoVisitsColumns()
    default:
      // Fallback to common columns + status
      return [
        ...getCommonColumns(),
        columnHelper.display({
          id: 'status',
          header: 'Met',
          cell: ({ row }) => (
            <Badge variant={getPassFailBadgeVariant(row.original.isMet)}>
              {row.original.isMet ? 'Met' : 'Unmet'}
            </Badge>
          ),
        }),
      ]
  }
}

/**
 * Get CSV export columns for a specific measure
 */
export function getCSVExportColumns(measureName: string): { header: string; accessor: (row: PatientDrilldownItem) => string }[] {
  const measure = measureName.toUpperCase()

  const commonColumns = [
    { header: 'Patient ID', accessor: (row: PatientDrilldownItem) => String(row.patientId) },
    { header: 'Patient Name', accessor: (row: PatientDrilldownItem) => row.patientName || row.providerName || 'Unknown' },
    { header: 'Facility', accessor: (row: PatientDrilldownItem) => row.facilityName || row.facilityId || 'Unknown' },
  ]

  let measureSpecificColumns: { header: string; accessor: (row: PatientDrilldownItem) => string }[] = []

  switch (measure) {
    case 'AWV':
      measureSpecificColumns = [
        { header: 'Eligible Month', accessor: (row) => row.awv || 'N/A' },
        { header: 'AWV Status', accessor: (row) => row.awvStatus || 'Unknown' },
        { header: 'Date Scheduled', accessor: (row) => formatDate(row.dateScheduled) },
        { header: 'Last AWV Date', accessor: (row) => formatDate(row.awvDate) },
      ]
      break
    case 'A1C':
      measureSpecificColumns = [
        { header: 'Last A1C Date', accessor: (row) => formatDate(row.a1cDate) },
        { header: 'A1C Value', accessor: (row) => (row.a1cValue ?? row.consolidatedA1cValue)?.toFixed(1) ?? 'N/A' },
        { header: 'Missing Component', accessor: (row) => row.diabetesMissingComponent || '-' },
      ]
      break
    case 'BCS':
      measureSpecificColumns = [
        { header: 'Last Screening Date', accessor: (row) => formatDate(getEarliestDate(row.bcsClaimsScreeningDate, row.bcsLabScreeningDate, row.bcsCounselingScreeningDate, row.bcsProgressScreeningDate)) },
        { header: 'BCS Status', accessor: (row) => row.bcsStatus || 'Unknown' },
        { header: 'Missing Component', accessor: (row) => row.bcsMissingComponent || '-' },
      ]
      break
    case 'CRCS':
      measureSpecificColumns = [
        { header: 'Last Screening Date', accessor: (row) => formatDate(row.crcsLastScreeningDate) },
        { header: 'Modality', accessor: (row) => row.crcsLastScreeningModality || '-' },
        { header: 'CRCS Status', accessor: (row) => row.crcsStatus || 'Unknown' },
        { header: 'Missing Component', accessor: (row) => row.crcsMissingComponent || '-' },
      ]
      break
    case 'PHQ9':
      measureSpecificColumns = [
        { header: 'Last PHQ9 Date', accessor: (row) => formatDate(row.lastPhq9Date) },
        { header: 'PHQ9 Score', accessor: (row) => String(row.phq9TotalScore ?? 'N/A') },
        { header: 'Psych Med Enrolled', accessor: (row) => {
          const pcpEnrolled = row.pcpEnrolledFlag
          const psychEnrolled = row.psychiatryEnrolledFlag
          return pcpEnrolled || psychEnrolled ? (pcpEnrolled && psychEnrolled ? 'Both' : pcpEnrolled ? 'PCP' : 'Psych') : 'Not Enrolled'
        }},
      ]
      break
    case 'HTN':
      measureSpecificColumns = [
        { header: 'Last BP Date', accessor: (row) => formatDate(row.lastBpDate) },
        { header: 'BP Reading', accessor: (row) => row.systolicBp && row.diastolicBp ? `${row.systolicBp}/${row.diastolicBp}` : 'N/A' },
        { header: 'Missing Component', accessor: (row) => row.htnMissingComponent || '-' },
      ]
      break
    case 'ACP':
      measureSpecificColumns = [
        { header: 'ACP Met Date', accessor: (row) => formatDate(row.acpMeasureMetDate) },
        { header: 'Missing Component', accessor: (row) => row.acpMissingComponent || '-' },
      ]
      break
    case 'ACO_VISITS':
      measureSpecificColumns = [
        { header: 'ACO Organization', accessor: (row) => row.acoName || '—' },
        { header: 'Assigned Physician', accessor: (row) => row.acoPhysicianAssigned || '—' },
        { header: 'Date Scheduled', accessor: (row) => formatDate(row.acoDateScheduled ?? undefined) },
        { header: 'Last Visit Date', accessor: (row) => formatDate(row.lastPcPhysicianVisit ?? undefined) },
        { header: 'Visit Status', accessor: (row) => row.acoVisitStatus || '—' },
      ]
      break
  }

  return [
    ...commonColumns,
    ...measureSpecificColumns,
    { header: 'Status', accessor: (row) => row.isMet ? 'Met' : 'Unmet' },
  ]
}
